import { useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  Search,
  Filter,
  Plus,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import EventCard from '../../components/events/EventCard';
import { useApi } from '../../contexts/ApiContext';
import { useSeries } from '../../App';

function Pagination({ currentPage, totalPages, onPageChange }) {
  const pages = [];
  const maxVisiblePages = 5;

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-between mt-4">
      <div className="text-xs text-slate-500">
        Page {currentPage} of {totalPages}
      </div>
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="xs"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>
        {startPage > 1 && (
          <>
            <Button
              variant={1 === currentPage ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => onPageChange(1)}
            >
              1
            </Button>
            {startPage > 2 && <span className="px-1.5 text-xs text-slate-400">...</span>}
          </>
        )}
        {pages.map((page) => (
          <Button
            key={page}
            variant={page === currentPage ? 'secondary' : 'ghost'}
            size="xs"
            onClick={() => onPageChange(page)}
          >
            {page}
          </Button>
        ))}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-1.5 text-xs text-slate-400">...</span>}
            <Button
              variant={totalPages === currentPage ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => onPageChange(totalPages)}
            >
              {totalPages}
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="xs"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function EventsList() {
  const { seriesSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const api = useApi();
  const currentSeries = useSeries();

  const hasDateRange = searchParams.has('start_date') || searchParams.has('end_date');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [dateFilter, setDateFilter] = useState(hasDateRange ? 'custom' : (searchParams.get('date') || 'upcoming'));
  const [showFilters, setShowFilters] = useState(false);

  const currentPage = parseInt(searchParams.get('page')) || 1;
  const seriesId = currentSeries?.dbId || currentSeries?.racingEntityId;

  const { data: eventsData, isLoading: loading, error: eventsError, refetch } = useQuery({
    queryKey: ['events', searchParams.toString(), seriesId],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: 10,
        sort: 'event_date',
        order: (dateFilter === 'upcoming' || dateFilter === 'custom') ? 'asc' : 'desc'
      };

      const searchVal = searchParams.get('search');
      const statusVal = searchParams.get('status');
      const startDate = searchParams.get('start_date');
      const endDate = searchParams.get('end_date');
      const dateVal = (startDate || endDate) ? 'custom' : (searchParams.get('date') || 'upcoming');
      if (searchVal) params.search = searchVal;
      if (statusVal) params.status = statusVal;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (dateVal === 'upcoming') params.upcoming = 'true';
      if (dateVal === 'past') params.past = 'true';
      if (seriesId) params.series_id = seriesId;

      const result = await api.events.getAll(params);
      if (!result.success) throw new Error(result.message || 'Failed to fetch events');
      return {
        events: result.data.events || [],
        pagination: result.data.pagination || { total: 0, page: 1, limit: 10, pages: 0 }
      };
    },
  });

  const events = eventsData?.events || [];
  const pagination = eventsData?.pagination || { total: 0, page: 1, limit: 10, pages: 0 };
  const error = eventsError?.message || null;

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    const params = new URLSearchParams(searchParams);
    params.set('date', value);
    params.set('page', '1');
    params.delete('start_date');
    params.delete('end_date');
    setSearchParams(params);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('status', value);
    } else {
      params.delete('status');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Events</h1>
            <p className="text-xs text-slate-500">
              {pagination.total} events found
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="xs"
              onClick={() => refetch()}
              disabled={loading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Link to={`/${seriesSlug}/events/new`}>
              <Button size="sm" leftIcon={Plus}>
                New Event
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card padding="compact" className="mb-4">
        <CardContent className="p-3">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <Input
                placeholder="Search events, tracks..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={Search}
                size="sm"
              />
            </form>

            {/* Quick Filters */}
            <div className="flex items-center gap-1.5">
              <Select
                value={dateFilter}
                onChange={(e) => handleDateFilterChange(e.target.value)}
                className="w-40"
                size="sm"
              >
                <option value="upcoming">Upcoming</option>
                <option value="past">Past</option>
                <option value="all">All Events</option>
                {dateFilter === 'custom' && (
                  <option value="custom">
                    {searchParams.get('start_date')?.slice(0, 4) || 'Custom'} Range
                  </option>
                )}
              </Select>

              <Button
                variant={showFilters ? 'secondary' : 'ghost'}
                size="xs"
                onClick={() => setShowFilters(!showFilters)}
                leftIcon={Filter}
              >
                Filters
                <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                size="sm"
              >
                <option value="">All Statuses</option>
                <option value="1">Scheduled</option>
                <option value="2">Confirmed</option>
                <option value="3">Completed</option>
                <option value="4">Cancelled</option>
                <option value="5">Postponed</option>
              </Select>

              <Input
                type="date"
                label="Start Date"
                size="sm"
                onChange={(e) => {
                  const params = new URLSearchParams(searchParams);
                  params.set('start_date', e.target.value);
                  setSearchParams(params);
                }}
              />

              <Input
                type="date"
                label="End Date"
                size="sm"
                onChange={(e) => {
                  const params = new URLSearchParams(searchParams);
                  params.set('end_date', e.target.value);
                  setSearchParams(params);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Events List */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Card key={i} padding="compact" className="animate-pulse">
              <CardContent className="p-2.5">
                <div className="flex gap-3">
                  <div className="w-11 h-12 bg-slate-200 rounded" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-1/3" />
                    <div className="h-3 bg-slate-200 rounded w-1/2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card padding="compact">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h3 className="text-md font-medium text-slate-800 mb-1.5">Error Loading Events</h3>
            <p className="text-xs text-slate-500 mb-3">{error}</p>
            <Button size="sm" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      ) : events.length === 0 ? (
        <Card padding="compact">
          <CardContent className="p-6 text-center">
            <Calendar className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <h3 className="text-md font-medium text-slate-800 mb-1.5">No Events Found</h3>
            <p className="text-xs text-slate-500 mb-3">
              {search ? 'Try adjusting your search or filters' : 'No events scheduled yet'}
            </p>
            <Link to={`/${seriesSlug}/events/new`}>
              <Button size="sm" leftIcon={Plus}>
                Create Event
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-2">
            {events.map((event) => (
              <EventCard key={event.id} event={event} seriesSlug={seriesSlug} />
            ))}
          </div>

          {pagination.pages > 1 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
}
